import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../store";
import { preloadSpaceSeries } from "./capacities.thunks";

export type CapacityMeta = {
  spaceId: number;
  start: string; // ISO date (startOfDay)
  end: string; // ISO date (startOfDay, exclusive)
  sizeId: number | null;
  excludeBookingId?: number | null;
};

export type CapacityDay = { date: string; am: number; pm: number; overall: number };

export type SeriesEntry = {
  meta: CapacityMeta;
  maxOverall: number;
  days: CapacityDay[]; // row‑aligned to [meta.start, meta.end)
  status?: "idle" | "loading" | "ready" | "error";
  error?: string | null;
  updatedAt?: number;
};

function keyOf(meta: CapacityMeta) {
  const ex = meta.excludeBookingId == null ? "" : `|ex=${meta.excludeBookingId}`;
  const size = meta.sizeId == null ? "" : `|sz=${meta.sizeId}`;
  return `${meta.spaceId}|${meta.start}|${meta.end}${size}${ex}`;
}

export { preloadSpaceSeries };

type State = {
  byKey: Record<string, SeriesEntry>;
  // For future: localDraftBySpace could live here; keeping it in component for now.
};

const initialState: State = {
  byKey: {},
};

const capacitiesSlice = createSlice({
  name: "capacities",
  initialState,
  reducers: {
    upsertSeries(state, action: PayloadAction<SeriesEntry>) {
      const k = keyOf(action.payload.meta);
      state.byKey[k] = { ...action.payload, status: action.payload.status ?? "ready", updatedAt: Date.now() };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(preloadSpaceSeries.pending, (state, action) => {
        const k = keyOf(action.meta.arg);
        const prev = state.byKey[k];
        state.byKey[k] = {
          meta: action.meta.arg,
          maxOverall: prev?.maxOverall ?? 0,
          days: prev?.days ?? [],
          status: "loading",
          updatedAt: prev?.updatedAt,
        } as any;
      })
      .addCase(preloadSpaceSeries.fulfilled, (state, action) => {
        const k = keyOf(action.payload.entry.meta);
        state.byKey[k] = { ...action.payload.entry, status: "ready", updatedAt: Date.now() };
      })
      .addCase(preloadSpaceSeries.rejected, (state, action) => {
        const k = keyOf(action.meta.arg);
        const prev = state.byKey[k];
        state.byKey[k] = { ...prev, status: "error", error: action.error?.message ?? "failed" } as any;
      });
  },
});

export const { upsertSeries } = capacitiesSlice.actions;
export const capacitiesReducer = capacitiesSlice.reducer;

// Selectors
export const makeKey = keyOf;
export const selectSeriesByKey = (state: RootState, key: string) => state.capacities.byKey[key];
